import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChefHat, Clock, DollarSign, TrendingUp, Zap, BarChart3, FileText, Package, Users, AlertCircle, MessageSquare, Calendar, Printer, Download, CheckCircle, Shield, Smartphone, Bell, CreditCard, Handshake, Rocket, Award } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageCaterersEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Caterers | Back-Office"; }, []);
  const [activePlan, setActivePlan] = useState('pro');

  return (
    <div className="home-container">
      <HeaderEN currentPage="caterers" lang="en" />

      {/* Hero Caterers */}
      <section className="hero-section caterers-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <ChefHat size={16} />
            <span>Caterers Back-Office</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-caterers)' }}>Caterers</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Save <strong style={{ color: 'var(--actor-caterers)' }}>15h/week</strong> per 100 meals/day<br />
            and increase your revenue <strong style={{ color: 'var(--actor-caterers)' }}>substantially</strong>
          </p>

          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">15h</div>
              <div className="stat-label">Saved/week<br />(100 meals/day)</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">+</div>
              <div className="stat-label">Substantial revenue</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Order errors</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto — Why EcoLunch for Caterers */}
      <section className="section" data-testid="manifeste-caterers">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch for Caterers?</h2>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-fondateur">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#F97316'}}><Handshake size={30} /></div>
              <h3>Free for all caterers serving fewer than 20 schools</h3>
            </div>
            <p>EcoLunch is <strong>completely free</strong> for any caterer serving fewer than 20 schools. No subscription fees, no support fees, no update fees. You get full access to the entire platform, including all features, updates and technical support, with no financial conditions whatsoever.</p>
            <p>By collaborating with us, you participate directly in product optimization: your feedback, needs and improvement requests help the platform evolve continuously, making it ever simpler and more efficient for your operational reality.</p>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-connect">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#22C55E'}}><Rocket size={30} /></div>
              <h3>A platform, but also a growth engine</h3>
            </div>
            <p>EcoLunch doesn't just provide access to a complete platform: it also provides access to a <strong>concrete commercial lever</strong>.</p>
            <p>Through EcoLunch Connect, we are already working with schools that want to offer hot meal services but don't yet do so due to operational complexity. By eliminating this barrier to entry, we help schools get started and enable partner caterers to expand their network.</p>
            <p>Joining EcoLunch is not just about adopting more modern technology. It's also about benefiting from the support of an ecosystem and a business development team that actively contribute to opening new doors.</p>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-fondation">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Award size={30} /></div>
              <h3>A serious, local and credible foundation</h3>
            </div>
            <p>The technological and partnership approach behind EcoLunch relies on recognized players:</p>
            <p><strong>Sezzle Canada</strong> — flexible payments<br /><strong>Paysafe Montreal</strong> — secure processing<br /><strong>OVH Cloud Quebec</strong> — sovereign hosting</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Caterers' challenges</h2>
            <p className="section-subtitle">Daily problems that slow down your business</p>
          </div>
          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Chaotic orders</h3>
              <p>Phone, email, outdated ordering platform. Orders come from everywhere. Impossible to consolidate efficiently.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><BarChart3 size={32} style={{color: '#84CC16'}} /></div>
              <h3>Excel or fragmented system</h3>
              <p>Multiple tools that don't communicate. Scattered data, double entries, inconsistencies.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><TrendingUp size={32} style={{color: '#84CC16'}} /></div>
              <h3>Inefficient prediction</h3>
              <p>Without predictive intelligence, your supply costs can exceed your actual needs.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon"><Clock size={32} style={{color: '#84CC16'}} /></div>
              <h3>Zero real-time visibility</h3>
              <p>Impossible to know where you stand in real time. Decisions made blindly.</p>
            </div>
          </div>
          <div className="solution-intro">
            <h3>EcoLunch eliminates these problems at the source</h3>
            <p>Our automated back-office turns your kitchen into a well-oiled machine. See how:</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Your intelligent back-office</h2>
            <p className="section-subtitle">
              Each feature designed to save you time and money
            </p>
          </div>

          <div className="benefits-grid">
            {/* Order Management */}
            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Automated order management</h3>
              <p>Real-time consolidation of all orders. Zero manual handling. Auto-generated PDF production sheets ready to print.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Automatic consolidation</h3>
              <p>All parent orders arrive directly in your system. Consolidated by school, by day, by menu.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Smart alerts</h3>
              <p>Abnormally high/low volumes? Late order? You are notified instantly.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Complete API for integration</h3>
              <p>Connect EcoLunch to all your internal systems (ERP, accounting, production) via our REST APIs for complete bidirectional synchronization.</p>
            </div>

            {/* Menu Management */}
            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Menu planning & inventory management</h3>
              <p>Create your menus once, manage everything else automatically. Intuitive interface for weekly/monthly menus with photos, descriptions, allergens.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>Automatic portion calculation</h3>
              <p>Define your recipes once. The system calculates the necessary quantities based on orders.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Allergen & restriction management</h3>
              <p>Automatic labeling. Alerts for children with dietary restrictions.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>Inventory & ingredient tracking</h3>
              <p>Optional module: track your inventory in real time, generate your shopping lists automatically.</p>
            </div>

            {/* Billing */}
            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Billing & revenue tracking</h3>
              <p>Automated accounting. Never a forgotten invoice again. Invoices generated automatically by school or by parent. Downloadable PDF.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Detailed financial reports</h3>
              <p>Revenue by school, by period, by menu. Exportable Excel/PDF.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><DollarSign size={32} /></div>
              <h3>Payment tracking</h3>
              <p>Dashboard of pending, received, overdue payments. Automatic reminders.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Revenue analytics</h3>
              <p>Charts and trends of your revenue. Identify your most profitable menus.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Sezzle Canada Partnership</h3>
              <p>By joining EcoLunch, you benefit from the commercial leverage and conversion gains linked to our exclusive partnership with Sezzle Canada.</p>
            </div>

            {/* Communication */}
            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Direct multi-actor communication</h3>
              <p>Integrated messaging to speak directly to schools and parents. Questions, modifications, alerts.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Direct school channel</h3>
              <p>Communicate instantly with schools. Questions, modifications, alerts.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Direct parent channel</h3>
              <p>Answer parent questions directly from your dashboard.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Real-time feedback</h3>
              <p>Receive children's ratings and comments live. Continuously improve your menus.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Incident management</h3>
              <p>Forgotten meal? Quality issue? Manage incidents quickly with the integrated ticket system.</p>
            </div>

            {/* Analytics */}
            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Analytics & optimization</h3>
              <p>Real-time dashboard: overview of your activity, today's orders, revenue, customer satisfaction.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Intelligent forecasting</h3>
              <p>AI that predicts future volumes based on history. Optimize your purchases.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Performance reports</h3>
              <p>Which menus work best? Which schools order the most? Everything is in the reports.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Download size={32} /></div>
              <h3>Complete data export</h3>
              <p>Export all your data in CSV/Excel for advanced analysis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Automation Message */}
      <section className="section roi-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="solution-explainer" style={{textAlign: 'center', maxWidth: '900px', margin: '0 auto'}}>
            <h2 style={{fontSize: '2.5rem', marginBottom: '1.5rem', color: 'var(--color-lime)'}}>
              EVERYTHING IS AUTOMATABLE
            </h2>
            <p style={{fontSize: '1.3rem', lineHeight: '1.8'}}>
              EcoLunch synchronizes with <strong>all your current management systems</strong>{' '}
              to eliminate double, triple data entries. Connect your ERP, 
              your accounting software, your production system: <strong>everything communicates together</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Premium™ Modules */}
      <section className="section features-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Premium™ Modules</h2>
            <p className="section-subtitle">
              Advanced technologies powered by proprietary AI
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>QPlanIQ™</h3>
              <p>Intelligent auto-ordering system optimized by proprietary AI. Automatic calculation of ingredients and purchases with predictive AI. Order predictions based on history and trends. Quantity optimization. Smart purchase suggestions according to seasons and availability.</p>
              <div style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'rgba(132, 204, 22, 0.1)', borderRadius: '8px', border: '1px solid var(--actor-schools)' }}>
                <strong style={{ color: 'var(--actor-schools)' }}>Coming - July 2026</strong>
              </div>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>ReportIQ™</h3>
              <p>Customization and automation of all reports. AI developed exclusively for food catering management with proprietary algorithms. 100% adapted to YOUR needs - no generic AI.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Package size={32} /></div>
              <h3>LogistIQ™</h3>
              <p>Intelligent delivery management powered by optimization AI. Route and schedule optimization with proprietary algorithms. Automated logistics planning. Complete real-time traceability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-caterers">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(249, 115, 22, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>for Caterers</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  One sentence is all you need
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">How many meals tomorrow for all schools?</div>
              <div className="ecopulse-mini-cmd">Revenue per school this week</div>
              <div className="ecopulse-mini-cmd">Launch label printing 8am tomorrow all schools</div>
              <div className="ecopulse-mini-cmd">Graph revenue by school and by month</div>
            </div>
            <button onClick={() => navigate('/en/ecopulse')} className="ecopulse-mini-link">
              Discover all EcoPulse commands <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageCaterersEN;
